import { ShieldExclamationIcon } from '@heroicons/react/solid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IconSvg, Modal, Text, Tooltip } from '@/lib/v2/components';
import { ErrorNewIcon, WarningNewIcon } from '@/lib/v2/icons/outline';
import Table, { createColumnHelper } from '@/lib/v3/components/Table/Table';

import { IResponseValidateHTML } from '@/modules/CampaignsModule/interfaces/HTMLEditor';

interface ModalValidateHTMLProps {
  isOpen: boolean;
  onClose: () => void;
  data: IResponseValidateHTML;
}

interface TableItem {
  line: number;
  column: number;
  message: string;
  type: string;
  tag: string;
}

const ModalValidateHTML = ({ isOpen, onClose, data }: ModalValidateHTMLProps) => {
  const { t } = useTranslation(['editorHtml']);

  const columnHelper = createColumnHelper<TableItem>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('line', {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        header: t('MODAL_VALIDATE_HTML.line') as string,
        cell: ({ row }) => {
          const { line } = row.original;
          return (
            <Text className="w-full text-center" variant="text-sm">
              {line}
            </Text>
          );
        },
      }),
      columnHelper.accessor('column', {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        header: t('MODAL_VALIDATE_HTML.column') as string,
        cell: ({ row }) => {
          const { column } = row.original;
          return (
            <Text className="w-full text-center" variant="text-sm">
              {column}
            </Text>
          );
        },
      }),
      columnHelper.accessor('message', {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        header: t('MODAL_VALIDATE_HTML.message') as string,
        maxSize: 300,

        cell: ({ row }) => {
          const { message } = row.original;
          return (
            <Tooltip
              isDark
              alignment="start"
              content={<div className="w-[290px] text-left text-12">{message}</div>}
              position="top"
            >
              <Text truncate className="w-full max-w-[290px] text-left" variant="text-sm">
                {message}
              </Text>
            </Tooltip>
          );
        },
      }),
      columnHelper.accessor('tag', {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        header: t('MODAL_VALIDATE_HTML.tag') as string,
        cell: ({ row }) => {
          const { tag } = row.original;
          return (
            <Text className="w-full text-center" variant="text-sm">
              {tag}
            </Text>
          );
        },
      }),
      columnHelper.accessor('type', {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        header: t('MODAL_VALIDATE_HTML.type') as string,
        cell: ({ row }) => {
          const { type } = row.original;
          return type === 'error' ? (
            <IconSvg height="24px" svgComponent={<ErrorNewIcon />} width="24px" />
          ) : (
            <IconSvg height="24px" svgComponent={<WarningNewIcon />} width="24px" />
          );
        },
      }),
    ],
    [t, columnHelper]
  );

  const tableData = useMemo(() => {
    const errors: TableItem[] = data.errors
      .map((error) => ({
        line: error.line,
        column: error.column,
        message: error.message,
        type: 'error',
        tag: error.tag,
      }))
      .sort((a, b) => a.line - b.line);

    const warnings = data.warnings
      .map((warning) => ({
        line: warning.line,
        column: warning.column,
        message: warning.message,
        type: 'warning',
        tag: warning.tag,
      }))
      .sort((a, b) => a.line - b.line);

    return [...errors, ...warnings];
  }, [data]);

  return (
    <Modal withAuto open={isOpen} showCloseButton={true} onClose={onClose}>
      <div className="flex w-full max-w-[750px] flex-col">
        <div className="mb-4 flex items-center gap-2">
          <IconSvg
            bgColor="destructive"
            height="40px"
            rounded={true}
            svgComponent={<ShieldExclamationIcon color="#992828" height={24} width={24} />}
            width="40px"
            withPadding="small"
          />
          <Text fontWeight="medium" variant="sub-headline">
            {t('MODAL_VALIDATE_HTML.title')}
          </Text>
        </div>
        <div className="flex flex-col gap-2">
          <Table enableScrollY columns={columns} data={tableData} enableScrollX={false} />
        </div>
      </div>
    </Modal>
  );
};

export default ModalValidateHTML;
